import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Plus, Radio } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SmsHubLayout } from "@/components/sms/SmsHubLayout";
import {
  listCampaigns,
  createCampaign,
  setCampaignStatus,
  setCampaignMode,
  listTwilioNumbers,
  listTemplates,
} from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/sms/campaigns")({
  head: () => ({ meta: [{ title: "Campaigns · SMS" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

const DAYS = [
  { v: 1, l: "Mon" },
  { v: 2, l: "Tue" },
  { v: 3, l: "Wed" },
  { v: 4, l: "Thu" },
  { v: 5, l: "Fri" },
  { v: 6, l: "Sat" },
  { v: 0, l: "Sun" },
];

function Page() {
  const { primaryRole } = useAuth();
  const qc = useQueryClient();
  const list = useServerFn(listCampaigns);
  const create = useServerFn(createCampaign);
  const setStatus = useServerFn(setCampaignStatus);
  const setMode = useServerFn(setCampaignMode);
  const fetchNumbers = useServerFn(listTwilioNumbers);
  const fetchTemplates = useServerFn(listTemplates);

  const { data, isLoading } = useQuery({
    queryKey: ["sms-campaigns"],
    queryFn: () => list(),
    enabled: primaryRole === "admin",
  });
  const { data: numbers } = useQuery({ queryKey: ["sms-numbers"], queryFn: () => fetchNumbers(), enabled: primaryRole === "admin" });
  const { data: tpls } = useQuery({ queryKey: ["sms-templates"], queryFn: () => fetchTemplates(), enabled: primaryRole === "admin" });

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [cap, setCap] = useState(5000);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [windowStart, setWindowStart] = useState("07:00");
  const [windowEnd, setWindowEnd] = useState("19:00");
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [selectedNumbers, setSelectedNumbers] = useState<string[] | null>(null);
  const [liveMode, setLiveMode] = useState(false);

  const numberIds = selectedNumbers ?? (numbers ?? []).filter((n: any) => n.enabled !== false).map((n: any) => n.id);

  const toggleDay = (d: number) =>
    setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort()));
  const toggleNumber = (id: string) => {
    const cur = selectedNumbers ?? (numbers ?? []).filter((n: any) => n.enabled !== false).map((n: any) => n.id);
    setSelectedNumbers(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const resetForm = () => {
    setName(""); setCap(5000); setStartDate(""); setEndDate("");
    setWindowStart("07:00"); setWindowEnd("19:00");
    setDays([1, 2, 3, 4, 5]); setSelectedNumbers(null); setLiveMode(false);
  };

  const createMut = useMutation({
    mutationFn: async () =>
      create({
        data: {
          name,
          daily_cap: cap,
          number_ids: numberIds,
          template_ids: (tpls ?? []).filter((t: any) => t.status === "approved").map((t: any) => t.id),
          start_date: startDate || null,
          end_date: endDate || null,
          window_start: windowStart.length === 5 ? `${windowStart}:00` : windowStart,
          window_end: windowEnd.length === 5 ? `${windowEnd}:00` : windowEnd,
          days_allowed: days,
          mode: liveMode ? "live" : "dry_run",
        },
      }),
    onSuccess: () => {
      toast.success(`Campaign created (${liveMode ? "LIVE" : "dry run"})`);
      setOpen(false);
      resetForm();
      qc.invalidateQueries({ queryKey: ["sms-campaigns"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => setStatus({ data: { id, status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sms-campaigns"] }),
  });

  const modeMut = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: "dry_run" | "live" }) => setMode({ data: { id, mode } }),
    onSuccess: (_d, v) => {
      toast.success(v.mode === "live" ? "Live SMS turned ON" : "Switched to dry run");
      qc.invalidateQueries({ queryKey: ["sms-campaigns"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed"),
  });

  return (
    <SmsHubLayout title="Campaigns">
      <div className="flex justify-end mb-3">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1.5" />New campaign</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create campaign</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start date</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div>
                  <Label>End date</Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Window start</Label>
                  <Input type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} />
                </div>
                <div>
                  <Label>Window end</Label>
                  <Input type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} />
                </div>
              </div>

              <div>
                <Label>Days of week</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {DAYS.map((d) => {
                    const on = days.includes(d.v);
                    return (
                      <button
                        key={d.v}
                        type="button"
                        onClick={() => toggleDay(d.v)}
                        className={`px-2.5 py-1 text-[12px] rounded-md border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground"}`}
                      >
                        {d.l}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Daily cap</Label>
                <Input type="number" value={cap} onChange={(e) => setCap(parseInt(e.target.value) || 0)} />
              </div>

              <div>
                <Label>Twilio numbers</Label>
                <div className="border border-border rounded-md p-2 max-h-40 overflow-y-auto space-y-1.5 mt-1">
                  {(numbers ?? []).length === 0 && (
                    <p className="text-[12px] text-muted-foreground">No numbers configured.</p>
                  )}
                  {(numbers ?? []).map((n: any) => {
                    const ids = selectedNumbers ?? (numbers ?? []).filter((x: any) => x.enabled !== false).map((x: any) => x.id);
                    const checked = ids.includes(n.id);
                    return (
                      <label key={n.id} className="flex items-center gap-2 text-[13px] cursor-pointer">
                        <Checkbox checked={checked} onCheckedChange={() => toggleNumber(n.id)} />
                        <span className="font-mono">{n.e164}</span>
                        {n.friendly_name && <span className="text-muted-foreground">— {n.friendly_name}</span>}
                        {n.enabled === false && <span className="text-[10px] text-muted-foreground">(disabled)</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between border border-border rounded-md px-3 py-2.5 bg-muted/30">
                <div>
                  <div className="text-[13px] font-medium flex items-center gap-1.5">
                    <Radio className={`h-3.5 w-3.5 ${liveMode ? "text-red-600" : "text-muted-foreground"}`} />
                    Live SMS
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {liveMode ? "Real messages will be sent." : "Dry run — no messages sent."}
                  </p>
                </div>
                <Switch checked={liveMode} onCheckedChange={setLiveMode} />
              </div>

              <Button disabled={!name || createMut.isPending} onClick={() => createMut.mutate()} className="w-full">
                {createMut.isPending ? "Creating…" : `Create ${liveMode ? "(LIVE)" : "(dry run)"}`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Daily cap</TableHead>
              <TableHead>Window</TableHead>
              <TableHead>Created</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={7} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
            )}
            {!isLoading && (data ?? []).length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No campaigns yet.</TableCell></TableRow>
            )}
            {(data ?? []).map((c: any) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell><span className="text-[11px] px-2 py-0.5 rounded-full bg-muted">{c.status}</span></TableCell>
                <TableCell>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${c.mode === "live" ? "bg-red-500/15 text-red-700" : "bg-amber-500/15 text-amber-700"}`}>
                    {c.mode}
                  </span>
                </TableCell>
                <TableCell>{c.daily_cap}</TableCell>
                <TableCell>{c.window_start?.slice(0, 5)}–{c.window_end?.slice(0, 5)}</TableCell>
                <TableCell className="text-[12px] text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right space-x-1">
                  {c.mode === "dry_run" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => {
                        if (confirm("Turn ON Live SMS? Real messages will be sent.")) {
                          modeMut.mutate({ id: c.id, mode: "live" });
                        }
                      }}
                    >
                      <Radio className="h-3 w-3 mr-1" />Go Live
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => modeMut.mutate({ id: c.id, mode: "dry_run" })}>
                      Dry run
                    </Button>
                  )}
                  {c.status !== "paused" && c.status !== "stopped" && (
                    <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ id: c.id, status: "paused" })}>Pause</Button>
                  )}
                  {c.status === "paused" && (
                    <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ id: c.id, status: "scheduled" })}>Resume</Button>
                  )}
                  {c.status !== "stopped" && (
                    <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ id: c.id, status: "stopped" })}>Stop</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SmsHubLayout>
  );
}
